import React, { useRef, useCallback, useEffect, useState, forwardRef } from 'react';
import { useLessonPause } from 'students/views/shared/hooks';
import ManualPlayButton from 'students/views/shared/components/ManualPlayButton';
import { useAudioPlayer } from 'students/views/shared/bundles/audio/hooks';
import PlayProgressButton from './PlayProgressButton';
import { IEventHandlerOptions } from '../../../bundles/audio/models';

interface IProps {
  src: string;
  color?: string;
  progressColor?: string;
  progressInterval?: number;
  className?: string;
  manualPlayClassName?: string;
  disabled?: boolean;
  autoplay?: boolean;
  autoPlayDelay?: number;
  showManualPlayButton?: boolean;
  onEnd?: (options: IEventHandlerOptions) => void;
  onAudioStopped?: () => void;
}

const SvgAudioPlayer = forwardRef<HTMLButtonElement, IProps>(
  (
    {
      src,
      color = '#3a3a8a',
      progressColor = '#ffffff',
      progressInterval = 100,
      autoplay,
      disabled,
      autoPlayDelay,
      className,
      manualPlayClassName,
      showManualPlayButton,
      onEnd,
      onAudioStopped
    },
    forwardedRef
  ) => {
    const [progress, setProgress] = useState(autoplay ? 0 : 100);
    const [isPlaying, setIsPlaying] = useState(false);
    const didCancel = useRef(false);

    const handleProgress = useCallback((progress: number) => {
      setProgress(progress);
    }, []);

    const handleStop = useCallback(() => {
      if (!didCancel.current) {
        setIsPlaying(false);
        setProgress(100);
        onAudioStopped && onAudioStopped();
      }
    }, [onAudioStopped]);

    const handlePlay = useCallback(() => {
      if (!didCancel.current) {
        setIsPlaying(true);
      }
    }, []);

    const handlePause = useCallback(() => {
      if (!didCancel.current) {
        setIsPlaying(false);
      }
    }, []);

    const handleEnd = useCallback(
      (options: IEventHandlerOptions) => {
        if (!didCancel.current) {
          setIsPlaying(false);
        }
        onEnd && onEnd(options);
      },
      [onEnd]
    );

    useEffect(() => {
      return () => {
        didCancel.current = true;
      };
    }, []);

    const { stop, togglePlay, cancelDelayedAutoplay } = useAudioPlayer({
      src,
      preload: true,
      progressInterval,
      autoplay,
      autoPlayDelay,
      onProgress: handleProgress,
      onPlay: handlePlay,
      onPause: handlePause,
      onStop: handleStop,
      onEnd: handleEnd
    });

    useLessonPause(stop);

    useEffect(() => {
      return () => {
        stop();
      };
    }, [stop]);

    const handleTogglePlay = () => {
      if (disabled) return;

      cancelDelayedAutoplay();
      togglePlay();
    };

    return (
      <div className="ln-relative ln-pt-16 ln-flex ln-flex-col ln-items-center">
        {showManualPlayButton && !disabled && !isPlaying && (
          <ManualPlayButton
            onClick={handleTogglePlay}
            className={`ln-mb-6 ln-h-6 -ln-mt-12 ${manualPlayClassName || ''}`.trim()}
          />
        )}
        <PlayProgressButton
          disabled={disabled}
          progress={progress}
          color={color}
          progressColor={progressColor}
          onClick={handleTogglePlay}
          className={className}
          ref={forwardedRef}
        />
      </div>
    );
  }
);

export default SvgAudioPlayer;
